import React, { Component, Fragment } from 'react';


import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faAngleUp, faClock } from '@fortawesome/free-solid-svg-icons';
import Collapse from 'react-bootstrap/Collapse';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';

import _ from 'lodash';

import Protected from '../auth/Protected';

import  { confirmDialog } from '../common/ConfirmDialog';
import i18n from './messages.json';
import dateUtil from '../../utils/date-util';
import { isPastScheduled } from '../../utils/schedule-helper';

const STATUS_STYLE = {
  delivered: 'success',
  draft:     'info',
  error:     'danger',
  dismissed: 'secondary',
  scheduled: 'warning',
};

// A scheduled push whose delivery time has passed has (best-effort) been sent by
// Braze — there is no FCM callback to flip the stored status, so surface it as
// delivered in the Activity Log while keeping the "Scheduled" origin visible.
// Display-only: the persisted status stays 'scheduled'.
const isDeliveredScheduled = item =>
  item.status === 'scheduled' && isPastScheduled(item);

class NotificationListItem extends Component {

  constructor(props) {
    super(props);

    this.state = {
      open: false,
    }
  }

  toggleCollapse = () => {
    this.setState({open: !this.state.open});
  }

  handleAction = action => {
    let msg, act;
    if(action === 'delete') {
      msg = i18n.ink.notification.confirmation.remove
      act = this.props.handleDelete;
    } else {
      msg = i18n.ink.notification.confirmation.dismiss;
      act = this.props.handleDismiss;
    }
    return async () => {
      try {
        await confirmDialog({message: msg});
        this.toggleCollapse();
        act(this.props.item);
      } catch(err) {
        // swallow it.
      }
    };
  }

  render() {
    const {item = {}, idx} = this.props;
    const {open} = this.state;

    const deliveredScheduled = isDeliveredScheduled(item);
    const displayStatus = deliveredScheduled ? 'delivered' : item.status;

    return (
      <Fragment key={'item' + idx}>
        <tr>
          <td>
            <div className="title">{item.title || item.body}</div>
          </td>
          <td>
            <Badge variant={STATUS_STYLE[displayStatus]}>
              {displayStatus.toUpperCase()}
            </Badge>
            {deliveredScheduled &&
              <span className="scheduled-origin-badge">
                <FontAwesomeIcon icon={faClock} className="scheduled-origin-badge__icon" />
                {i18n.ink.notification.scheduled.badgeScheduled}
              </span>}
          </td>
          <td>{_.get(item, 'user.name', i18n.ink.notification.notAvailable)}</td>
          {/* Delivery time: a scheduled push shows when it was scheduled to send;
              a normal push shows when it was created (≈ sent immediately). */}
          <td>{dateUtil.formatEtDateTime(item.scheduledSendTime || item.created_at)}</td>
          <td>
            <span className="collapse-action" onClick={this.toggleCollapse}>
              <FontAwesomeIcon icon={open ? faAngleDown : faAngleUp} />
            </span>
          </td>
        </tr>
        <tr className={open ? 'collapse-tr-show' : 'collapse-tr-hide'}>
          <td colSpan="5">
            {this._renderDetails(item, open, idx)}
          </td>
        </tr>
      </Fragment>
    );
  }

  _renderDetails(item, open, idx) {
    let {title, body, targeting} = item;

    let comments;
    try {
      comments = JSON.parse(item.comments);
    } catch {
      comments = {};
    }

    if(item.status === 'error') {
      // We got different response when send notification to mutiple devices
      // so we need handle the respone
      const fbResponse = _.get(comments, 'fbResponse', {});
      const responses = _.get(fbResponse, 'responses');
      if(Array.isArray(responses)) {
        title =  this._renderErrorTitle(fbResponse);
        body = this._renderErrorBody(responses, idx);
      } else {
        title = _.get(fbResponse, 'code');
        body = _.get(fbResponse, 'message', i18n.ink.notification.noErrorMessageFound);
      }
    }

    const tag = _.get(comments, 'tag');
    const target = _.get(targeting, 'targetType.label');
    let subTarget = _.get(targeting, 'selected', []);

    if (target === 'Device') {
      subTarget = _.truncate(subTarget, 15);
    }

    // brazeResults is either the current single object (one dispatch OR'd over
    // every segment) or the per-campaign array (one entry each). Segment names
    // render the same for both. An immediate send carries a Braze `dispatchId`; a
    // scheduled push carries a `scheduleId` instead (a dispatch id only exists
    // once Braze delivers), so surface whichever is present and label it to match.
    const brazeRaw = item.brazeResults;
    const brazeSegments = _.isArray(brazeRaw)
      ? brazeRaw.map(r => r.name).filter(Boolean)
      : _.get(brazeRaw, 'names', []);
    const brazeIds = _.isArray(brazeRaw)
      ? brazeRaw.map(r => ({ name: r.name, id: r.dispatchId || r.scheduleId }))
      : (brazeRaw ? [{ id: brazeRaw.dispatchId || brazeRaw.scheduleId }] : []);
    const hasScheduleIds = _.isArray(brazeRaw)
      ? brazeRaw.some(r => !r.dispatchId && r.scheduleId)
      : Boolean(brazeRaw && !brazeRaw.dispatchId && brazeRaw.scheduleId);
    const brazeIdLabel = hasScheduleIds ? 'Braze Schedule ID' : 'Braze Dispatch ID';

    const isError = item.status === 'error';
    const hasSubTargets = Array.isArray(subTarget) ? subTarget.length > 0 : Boolean(subTarget);

    return (
      <Collapse in={open}>
        <div className='activity-detail'>
          {/* Message preview — the push as the reader would see it. */}
          <div className={`activity-detail__message${isError ? ' activity-detail__message--error' : ''}`}>
            <div className='activity-detail__message-title'>{title}</div>
            {body ? <div className='activity-detail__message-body'>{body}</div> : null}
          </div>

          {/* Metadata grid */}
          <dl className='activity-detail__meta'>
            <div className='activity-detail__meta-item'>
              <dt className='activity-detail__meta-label'>Target</dt>
              <dd className='activity-detail__meta-value'>
                <span className='activity-detail__pill activity-detail__pill--target'>
                  {target || i18n.ink.notification.notAvailable}
                </span>
                {hasSubTargets && (
                  target !== 'Device'
                    ? subTarget.map((_subTarget, i) => (
                        <span key={`target-value-${i}`} className='activity-detail__pill'>{_subTarget}</span>
                      ))
                    : <span className='activity-detail__code'>{subTarget}</span>
                )}
              </dd>
            </div>

            {item.created_at && (
              <div className='activity-detail__meta-item'>
                <dt className='activity-detail__meta-label'>Created</dt>
                <dd className='activity-detail__meta-value'>{dateUtil.formatEtDateTime(item.created_at)}</dd>
              </div>
            )}

            {tag && (
              <div className='activity-detail__meta-item'>
                <dt className='activity-detail__meta-label'>Analytics Label</dt>
                <dd className='activity-detail__meta-value'>
                  <span className='activity-detail__code'>{tag}</span>
                </dd>
              </div>
            )}

            {brazeSegments.length > 0 && (
              <div className='activity-detail__meta-item activity-detail__meta-item--wide'>
                <dt className='activity-detail__meta-label'>Braze Segments</dt>
                <dd className='activity-detail__meta-value'>
                  {brazeSegments.map((name, i) => (
                    <span key={`braze-seg-${i}`} className='activity-detail__pill activity-detail__pill--braze'>{name}</span>
                  ))}
                </dd>
              </div>
            )}

            {brazeIds.length > 0 && (
              <div className='activity-detail__meta-item activity-detail__meta-item--wide'>
                <dt className='activity-detail__meta-label'>{brazeIdLabel}</dt>
                <dd className='activity-detail__meta-value'>
                  {brazeIds.map((d, i) => (
                    <span key={`braze-id-${i}`} className='activity-detail__mono-item'>
                      {d.name ? <span className='activity-detail__mono-name'>{d.name}</span> : null}
                      <span className='activity-detail__code'>{d.id || i18n.ink.notification.notAvailable}</span>
                    </span>
                  ))}
                </dd>
              </div>
            )}
          </dl>

          <Protected privilege='notification:publish'>
            {this._renderActionButtons(item)}
          </Protected>
        </div>
      </Collapse>
    );
  }

  _renderActionButtons(item) {
    if(item.status === 'delivered') {
      return <Fragment />;
    }

    const editBtn = (
      <Button onClick={() => this.props.openEditNotification(item.id)} variant="primary" size="sm">
        {i18n.ink.notification.buttons.edit}
      </Button>
    );

    const deleteBtn = (
      <Button onClick={this.handleAction('delete')} variant="outline-danger" size="sm">
        {i18n.ink.notification.buttons.remove}
      </Button>
    );

    const dismissBtn = (
      <Button onClick={this.handleAction('dismiss')} variant="outline-secondary" size="sm">
        {i18n.ink.notification.buttons.dismiss}
      </Button>
    );

    if(item.status === 'draft') {
      return <div className="activity-detail__actions">{deleteBtn}{editBtn}</div>;
    }

    if(item.status === 'error') {
      return <div className="activity-detail__actions">{dismissBtn}{editBtn}</div>;
    }

    return null;
  }

  _renderErrorTitle(fbResponse) {
    return <p> {i18n.ink.notification.successCount + fbResponse.successCount} <br/>
                {i18n.ink.notification.failureCount + fbResponse.failureCount}
            </p>;
  }
  _renderErrorBody(responses, idx) {

   return responses.map(
      (res, index) => {
          return  (
            <div className="msg-body-error" key={'error-'+idx +'-'+index}>
              <h6>{ _.get(res, 'token')}</h6>
              <p className={ res.success ? 'msg-success' : 'msg-error'}> {_.get(res, 'message')  }</p>
            </div>
          )

      }
    )
  }
}

export default NotificationListItem;
